import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { Action } from 'src/app/core/models/action.model';
import { DatabaseService } from 'src/app/core/services/base/database.service';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import { UserService } from 'src/app/core/services/user.service';
import { notesAllowProceed, isAgentSkipCheck, AGENT_SKIP_STAGE, AGENT_SKIP_FLAG } from 'src/app/core/utils/kyc-postpone.util';
import { isQuestionnaireSkippedInSession } from 'src/app/shared/utils/elective';

/**
 * Resultado da verificação de estado de uma action
 */
export interface ActionStateResult {
  actionIndex: number;
  action: Action;
  isComplete: boolean;
  status: string | null;
  passed?: boolean | null; // Para KYC
}

/**
 * Estado do wizard no momento
 */
export interface WizardState {
  currentActionIndex: number;
  currentAction: Action;
  allActionsComplete: boolean;
  claimIsActive: boolean;
}

/**
 * Interface para o campo attributes do contrato
 */
interface ContractAttributes {
  previous_versions?: string[];
  [key: string]: any;
}

/**
 * Interface para dados do contrato
 */
interface ContractData {
  id: string;
  attributes?: ContractAttributes;
}

/**
 * Service para gerenciar estado do wizard de forma eficiente
 * Evita múltiplas requisições ao verificar apenas o estado atual necessário
 */
@Injectable({
  providedIn: 'root',
})
export class ManagementStepService extends DatabaseService<any> {
  constructor(private agentClientsService: AgentClientsService, private userService: UserService) {
    super('cp_claim'); // Tabela base, mas usaremos queries específicas
  }

  /**
   * Verifica o estado atual do wizard e retorna qual action está incompleta
   * 
   * ✅ RESPONSABILIDADE COMPLETA: Verifica status de cada action conforme itera
   * ✅ Verifica se pré-requisitos estão completos antes de verificar a action
   * ✅ Atualiza os campos ac_status, status, done_action_id nas actions
   * ✅ Para na primeira action incompleta (otimizado)
   * 
   * @param actions - Lista de actions ordenadas do wizard (SEM status preenchidos)
   * @param claimId - ID do claim
   * @param userId - ID do usuário responsável
   * @returns Estado atual do wizard com a primeira action incompleta
   */
  async getState(
    actions: Action[],
    claimId: string,
    userId: string
  ): Promise<WizardState> {
    console.log('🔎 [WIZARD_DEBUG] GET_STATE_START', {
      totalActions: actions.length,
      claimId,
      userId,
      inputOrder: actions.map((a, i) => ({
        step: i,
        name: a.action_name,
        type: a.action_type,
        caseActionId: a.case_action_id?.slice(0,8),
        prstatus: a.prstatus,
        prerequisite_ids: (a.prerequisite_ids || []).map((id: string) => id.slice(0,8)),
      })),
    });

    // Verifica se claim está ativo antes de processar
    const claimIsActive = await this.isClaimActive(claimId);

    if (!claimIsActive) {
      console.warn('[MANAGEMENT_STEP] Claim is INACTIVE', { claimId });
      return {
        currentActionIndex: 0,
        currentAction: actions[0],
        allActionsComplete: false,
        claimIsActive: false,
      };
    }

    // Loop através das actions até encontrar a primeira incompleta
    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];

      console.log('🔎 [WIZARD_DEBUG] GET_STATE_CHECK_ACTION', {
        index: i,
        actionId: action.action_id,
        actionType: action.action_type,
        actionName: action.action_name,
        caseActionId: action.case_action_id?.slice(0,8),
        prstatus: action.prstatus,
        prerequisite_ids: (action.prerequisite_ids || []).map((id: string) => id.slice(0,8)),
        ac_status: action.ac_status,
      });

      // 1️⃣ Se tem pré-requisitos (prstatus = 'OPEN'), verifica se estão completos
      if (action.prstatus === 'OPEN') {
        console.log('🔎 [WIZARD_DEBUG] PREREQ_CHECK_START', {
          index: i,
          actionId: action.action_id,
          actionName: action.action_name,
          prerequisite_ids: (action.prerequisite_ids || []).map((id: string) => id.slice(0,8)),
        });

        const prereqsComplete = await this.checkPrerequisitesComplete(action, actions, claimId, userId);
        
        if (!prereqsComplete) {
          console.log('🔎 [WIZARD_DEBUG] PREREQ_BLOCKED — returning this step as current', {
            index: i,
            actionId: action.action_id,
            actionName: action.action_name,
          });
          
          // Marca action como bloqueada por pré-requisitos
          action.ac_status = 'BLOCKED';
          action.status = 'BLOCKED';
          
          return {
            currentActionIndex: i,
            currentAction: action,
            allActionsComplete: false,
            claimIsActive: true,
          };
        }
        
        // ✅ Pré-requisitos completos, atualiza prstatus
        console.log('🔎 [WIZARD_DEBUG] PREREQ_ALL_DONE', { index: i, actionId: action.action_id });
        action.prstatus = 'DONE';
      }

      // 2️⃣ Verifica status da própria action E atualiza os campos
      const checkResult = await this.checkAndFillActionStatus(action, claimId, userId);

      console.log('🔎 [WIZARD_DEBUG] ACTION_STATUS_RESULT', {
        index: i,
        actionId: action.action_id,
        actionName: action.action_name,
        actionType: action.action_type,
        isComplete: checkResult.isComplete,
        ac_status: checkResult.ac_status,
        done_action_id: checkResult.done_action_id,
      });

      if (!checkResult.isComplete) {
        // Encontrou a primeira action incompleta
        console.log('🔎 [WIZARD_DEBUG] FIRST_INCOMPLETE_FOUND — returning this step as current', {
          index: i,
          actionId: action.action_id,
          actionName: action.action_name,
          actionType: action.action_type,
        });
        return {
          currentActionIndex: i,
          currentAction: action,
          allActionsComplete: false,
          claimIsActive: true,
        };
      }
    }

    // Todas as actions estão completas
    console.log('🔎 [WIZARD_DEBUG] ALL_ACTIONS_COMPLETE', {
      totalActions: actions.length,
      finalOrder: actions.map((a, i) => ({ step: i, name: a.action_name, type: a.action_type, ac_status: a.ac_status })),
    });
    return {
      currentActionIndex: actions.length - 1,
      currentAction: actions[actions.length - 1],
      allActionsComplete: true,
      claimIsActive: true,
    };
  }

  /**
   * Verifica se os pré-requisitos de uma action estão completos
   * ✅ Usa prerequisite_ids ou case_action_id para encontrar pré-requisitos
   * 
   * @returns true se todos os pré-requisitos estão completos
   */
  private async checkPrerequisitesComplete(
    action: Action,
    allActions: Action[],
    claimId: string,
    userId: string
  ): Promise<boolean> {
    // Pega lista de prerequisite IDs (populado pelo getOutstandingActionsV3 → convertEnrichedToAction)
    const prerequisiteIds: string[] = action.prerequisite_ids || [];
    
    if (prerequisiteIds.length === 0) {
      console.log('🔎 [WIZARD_DEBUG] PREREQ_EMPTY — no prerequisite_ids', { actionId: action.action_id });
      return true;
    }

    console.log('🔎 [WIZARD_DEBUG] PREREQ_CHECKING_IDS', {
      actionId: action.action_id,
      actionName: action.action_name,
      prerequisiteIds: prerequisiteIds.map(id => id.slice(0,8)),
      totalPrereqs: prerequisiteIds.length,
    });

    // Verifica cada pré-requisito
    for (const prereqCaseActionId of prerequisiteIds) {
      // Encontra a action correspondente ao pré-requisito
      const prereqAction = allActions.find(a => a.case_action_id === prereqCaseActionId);
      
      if (!prereqAction) {
        console.warn('🔎 [WIZARD_DEBUG] PREREQ_ACTION_NOT_FOUND', {
          prereqCaseActionId: prereqCaseActionId.slice(0,8),
          actionId: action.action_id,
          availableCaseActionIds: allActions.map(a => a.case_action_id?.slice(0,8)),
        });
        return false;
      }

      // Se ainda não verificou status do pré-requisito, verifica agora
      if (prereqAction.ac_status === null || prereqAction.ac_status === undefined) {
        await this.checkAndFillActionStatus(prereqAction, claimId, userId);
      }

      // Verifica se pré-requisito está completo (passa userId para considerar agent mode)
      const isPrereqComplete = this.isActionCompleteByStatus(prereqAction, userId, claimId);
      
      console.log('🔎 [WIZARD_DEBUG] PREREQ_INDIVIDUAL_CHECK', {
        prereqCaseActionId: prereqCaseActionId.slice(0,8),
        prereqActionId: prereqAction.action_id,
        prereqActionName: prereqAction.action_name,
        prereqActionType: prereqAction.action_type,
        prereqStatus: prereqAction.ac_status,
        isAgentMode: prereqAction.action_type === 'KYC' ? this.agentClientsService.isAgentSession() : undefined,
        isComplete: isPrereqComplete,
      });

      if (!isPrereqComplete) {
        return false;
      }
    }

    console.log('🔎 [WIZARD_DEBUG] PREREQ_ALL_PASSED', {
      actionId: action.action_id,
      actionName: action.action_name,
      totalPrereqs: prerequisiteIds.length,
    });
    
    return true;
  }

  /**
   * Verifica se uma action está completa baseado no ac_status
   * ✅ Usado para verificar pré-requisitos
   * ✅ Considera agent mode para KYC
   * ✅ CORRIGIDO: Para KYC não-agent, verifica se realmente passou (não apenas se ac_status = DONE)
   */
  /**
   * CPK-4744 (correção de escopo): `deferred_claims` é um array de claim_id —
   * cp_case_actions é uma linha por CASE (compartilhada por todos os claims
   * daquele case), então um boolean único deferiria KYC/Signature pra todo
   * mundo do case. Escopar por claim_id evita esse vazamento.
   */
  private isDeferredForClaim(action: Action): boolean {
    const deferredClaims = action.case_action_config?.simple_questionnaire?.deferred_claims;
    return Array.isArray(deferredClaims) && !!action.claim_id && deferredClaims.includes(action.claim_id);
  }

  /**
   * CPK-4878: questionário que o agente deixou OPEN com puláveis vazias nesta
   * sessão Simple Questionnaire — conta como feito só aqui, para o agente seguir
   * para KYC/assinatura. Fora da sessão (cliente, outro acesso) volta a pendente.
   */
  private isQuestionnaireSkippedThisSession(action: Action, claimId?: string): boolean {
    const claim = claimId ?? action.claim_id;
    if (action.action_type !== 'QUESTIONNAIRE' || !claim || !action.questionnaire_id) return false;
    return (
      this.agentClientsService.isSimpleQuestionnaireSession() &&
      isQuestionnaireSkippedInSession(claim, action.questionnaire_id)
    );
  }

  /**
   * CPK-5056: na sessão de agente o KYC não precisa terminar para o wizard
   * seguir (CPK-2613 — o agente não espera um check em PROCESSING), mas um
   * check reprovado sem saída continua bloqueando, como no gate do backend
   * (prerequisiteCheck.ts). `FAILED` só sai de checkAndFillKycStatus quando o
   * check é DONE + passed=false sem postpone, skip de agente ou bypass.
   */
  private isKycCompleteForAgent(doneActionId: string | null | undefined, acStatus: string | null | undefined): boolean {
    return !!doneActionId && acStatus !== 'FAILED';
  }

  private isActionCompleteByStatus(action: Action, userId?: string, claimId?: string): boolean {
    // CPK-4744: KYC/Signature diferidos (Simple Questionnaire) não bloqueiam,
    // mesmo quando esta action é pré-requisito de outra. ac_status/done_action_id
    // seguem intocados — a pendência real continua visível em outras telas.
    const deferred = this.isDeferredForClaim(action);
    if (deferred && (action.action_type === 'KYC' || action.action_type === 'SIGNATURE')) {
      return true;
    }

    switch (action.action_type) {
      case 'QUESTIONNAIRE':
        // CPK-4878: também no caminho de pré-requisito — em Santo Amaro o KYC
        // depende do questionário de identificação.
        return (
          action.ac_status === 'DONE' ||
          action.ac_status === 'SUBMITTED' ||
          this.isQuestionnaireSkippedThisSession(action, claimId)
        );

      case 'KYC':
        // Agent mode: completo se iniciou o KYC (done_action_id) e não reprovou (CPK-5056)
        // Normal: completo APENAS se ac_status === 'DONE' E tem done_action_id válido
        // O campo 'passed' do cp_kyc_check é validado em checkAndFillKycStatus()
        // que já preencheu ac_status corretamente considerando passed=true/false
        if (userId) {
          const isAgentMode = this.agentClientsService.isAgentSession();
          if (isAgentMode) {
            return this.isKycCompleteForAgent(action.done_action_id, action.ac_status);
          } else {
            // Normal: ac_status deve ser DONE (que só é setado quando passed=true)
            // Se passed=false, ac_status ficou como PENDING ou outro status
            return action.ac_status === 'DONE';
          }
        }
        return action.ac_status === 'DONE';

      case 'SIGNATURE':
        return action.ac_status === 'SIGNED';

      default:
        return false;
    }
  }

  /**
   * Verifica status da action E preenche os campos ac_status, status, done_action_id
   * ✅ Mutação controlada: Atualiza a action passada por referência
   * 
   * @returns Objeto com isComplete e dados do status
   */
  private async checkAndFillActionStatus(
    action: Action,
    claimId: string,
    userId: string
  ): Promise<{
    isComplete: boolean;
    ac_status: string | null;
    done_action_id: string | null;
    status: string | null;
    checkedRow?: { id: string; stage: string | null; passed: boolean | null; notes?: unknown; created_at: string | null } | null;
  }> {
    // ✅ OTIMIZAÇÃO: Se já tem status preenchido, não busca no banco novamente
    // Isso evita chamadas duplicadas quando o GuideService já verificou a action
    if (action.ac_status !== null && action.ac_status !== undefined) {
      // Para KYC, precisa verificar agent mode e bypass mesmo com cache
      let isComplete: boolean;
      const deferred = this.isDeferredForClaim(action);
      if (deferred && (action.action_type === 'KYC' || action.action_type === 'SIGNATURE')) {
        // CPK-4744: mesmo já cacheada (ac_status preenchido), uma action diferida
        // não bloqueia o wizard. ac_status não é tocado.
        isComplete = true;
      } else if (action.action_type === 'KYC') {
        const isAgentMode = this.agentClientsService.isAgentSession();
        const currentUser = await this.userService.getCurrentUser();
        const bypassedByAgent = !!(currentUser?.attributes as any)?.kyc?.bypassedByAgent;
        // Agent mode: completo se tem done_action_id e não reprovou (CPK-5056)
        // Bypassed: completo independente do status
        // Normal: completo se ac_status === 'DONE'
        isComplete = isAgentMode
          ? this.isKycCompleteForAgent(action.done_action_id, action.ac_status)
          : bypassedByAgent || action.ac_status === 'DONE';
      } else {
        isComplete = this.isActionCompleteByStatus(action, undefined, claimId);
      }
      
      console.log('🔎 [WIZARD_DEBUG] STATUS_CACHED', {
        actionId: action.action_id,
        actionName: action.action_name,
        actionType: action.action_type,
        ac_status: action.ac_status,
        isAgentMode: action.action_type === 'KYC' ? this.agentClientsService.isAgentSession() : undefined,
        isComplete,
      });
      return {
        isComplete,
        ac_status: action.ac_status,
        done_action_id: action.done_action_id,
        status: action.status
      };
    }

    switch (action.action_type) {
      case 'QUESTIONNAIRE':
        return await this.checkAndFillQuestionnaireStatus(action, claimId, userId);

      case 'KYC':
        return await this.checkAndFillKycStatus(action, claimId, userId);

      case 'SIGNATURE':
        return await this.checkAndFillSignatureStatus(action, claimId, userId);

      default:
        console.warn('[MANAGEMENT_STEP] Unknown action type', {
          actionType: action.action_type,
        });
        return { isComplete: false, ac_status: null, done_action_id: null, status: null };
    }
  }

  /**
   * Verifica se questionário está completo E preenche status na action
   * Query: questionnaire_id + claim_id + responsible_user_id → status
   */
  private async checkAndFillQuestionnaireStatus(
    action: Action,
    claimId: string,
    userId: string
  ): Promise<{ isComplete: boolean; ac_status: string | null; done_action_id: string | null; status: string | null }> {
    if (!action.questionnaire_id) {
      console.warn('[MANAGEMENT_STEP] Questionnaire action without questionnaire_id', {
        actionId: action.action_id,
      });
      return { isComplete: false, ac_status: null, done_action_id: null, status: null };
    }

    try {
      const { data, error } = await this.supabase
        .from('cp_questionnaire_answer')
        .select('id, status')
        .eq('questionnaire_id', action.questionnaire_id)
        .eq('claim_id', claimId)
        //.eq('responsible_user_id', userId)
        .in('status', ['DONE', 'SUBMITTED'])
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[MANAGEMENT_STEP] Error checking questionnaire', {
          error,
          questionnaireId: action.questionnaire_id,
        });
        return { isComplete: false, ac_status: null, done_action_id: null, status: null };
      }

      // CPK-4878: ac_status segue o banco (null para OPEN) — só o isComplete muda.
      const isComplete = !!data || this.isQuestionnaireSkippedThisSession(action, claimId);
      const ac_status = data?.status || null; // ✅ Mantém SUBMITTED ou DONE
      const done_action_id = data?.id || null;
      const status = data?.status || null;

      // ✅ Preenche os campos na action
      action.ac_status = ac_status;
      action.done_action_id = done_action_id;
      action.status = status;

      console.log('🔎 [WIZARD_DEBUG] QUESTIONNAIRE_STATUS_CHECK', {
        actionId: action.action_id,
        actionName: action.action_name,
        questionnaireId: action.questionnaire_id,
        claimId,
        userId,
        dbStatus: data?.status || null,
        isComplete,
      });

      return { isComplete, ac_status, done_action_id, status };
    } catch (error) {
      console.error('🔎 [WIZARD_DEBUG] QUESTIONNAIRE_STATUS_ERROR', {
        error,
        actionId: action.action_id,
        questionnaireId: action.questionnaire_id,
      });
      return { isComplete: false, ac_status: null, done_action_id: null, status: null };
    }
  }

  /**
   * Verifica se KYC está completo E aprovado, E preenche status na action
   * Query: checked_user_id → stage + passed
   */
  async checkAndFillKycStatus(
    action: Action,
    claimId: string,
    userId: string
  ): Promise<{
    isComplete: boolean;
    ac_status: string | null;
    done_action_id: string | null;
    status: string | null;
    checkedRow: { id: string; stage: string | null; passed: boolean | null; notes?: unknown; created_at: string | null } | null;
  }> {
    if (!action.action_id) {
      console.warn('[MANAGEMENT_STEP] KYC action without action_id', {
        actionName: action.action_name,
      });
      return { isComplete: false, ac_status: null, done_action_id: null, status: null, checkedRow: null };
    }

    try {
      // Agent mode: verifica primeiro se usuário está em agent mode
      const isAgentMode = this.agentClientsService.isAgentSession();

      // Regra: se o usuário já tem qualquer kyc_check com passed=true para algum
      // provider configurado nessa action, considera aprovado — independente de
      // tentativas posteriores. Só cai no "mais recente qualquer" quando nunca
      // passou (pra exibir o erro correto na UI). NÃO usar `order('id', desc)`
      // aqui — UUIDs ordenam por texto e devolveriam um check arbitrário.
      //
      // Filtra por `provider IN action.config.providers` (mesma semântica da
      // view cpv_actions_done). Não filtra por action_id porque alguns flows
      // criam cp_kyc_check com action_id NULL (ex: esign do PoA), e
      // mesmo assim casam com essa action via o array de providers.
      const providers: string[] = (action.config && Array.isArray(action.config.providers))
        ? action.config.providers
        : [];

      if (providers.length === 0) {
        console.warn('[MANAGEMENT_STEP] KYC action has no providers configured', {
          actionId: action.action_id,
          actionName: action.action_name,
        });
        return { isComplete: false, ac_status: null, done_action_id: null, status: null, checkedRow: null };
      }

      let data: { id: string; stage: string | null; passed: boolean | null; notes?: unknown; created_at: string | null } | null = null;

      const { data: passedRow, error: passedError } = await this.supabase
        .from('cp_kyc_check')
        .select('id, stage, passed, notes, created_at')
        .eq('checked_user_id', userId)
        .in('provider', providers)
        .eq('passed', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (passedError) {
        console.error('[MANAGEMENT_STEP] Error fetching passed cp_kyc_check', {
          error: passedError,
          actionId: action.action_id,
          userId,
        });
        return { isComplete: false, ac_status: null, done_action_id: null, status: null, checkedRow: null };
      }

      if (passedRow) {
        data = passedRow;
      } else {
        // CPK-3705: cross-case reuse — if no provider-specific passed check exists,
        // fall back to any approved check for this user (mirrors prerequisiteCheck.ts
        // backend logic which does not filter by provider).
        const { data: anyPassedRow, error: anyPassedError } = await this.supabase
          .from('cp_kyc_check')
          .select('id, stage, passed, notes, created_at')
          .eq('checked_user_id', userId)
          .eq('stage', 'DONE')
          .eq('passed', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (anyPassedError) {
          console.error('[MANAGEMENT_STEP] Error fetching cross-case cp_kyc_check', {
            error: anyPassedError,
            actionId: action.action_id,
            userId,
          });
        }

        if (!anyPassedError && anyPassedRow) {
          console.log('[MANAGEMENT_STEP] KYC cross-case reuse: found approved check from another case', {
            actionId: action.action_id,
            kycCheckId: anyPassedRow.id,
            userId,
          });
          data = anyPassedRow;
        } else {
          // Nenhum check aprovado — pega o mais recente pra refletir o estado real
          // (PROCESSING / FAILED) na UI.
          // CPK-4012: escopar por claim. Um check NÃO-aprovado (falhou / postpone) só
          // deve gatear ESTE claim. Sem isso, um check reprovado+postergado do claim A
          // vazava para o claim B (canProceed=true → "Proceed" desabilitado). Forma
          // segura p/ um path compartilhado com prod: casa checks DESTE claim OU legados
          // sem claim_id (que não têm binding — mantidos pra não regredir dados antigos);
          // checks NOVOS de OUTRO claim (claim_id != claimId) são excluídos. O reuso de
          // check APROVADO segue cross-claim acima (CPK-3705 / AC#2).
          const { data: latestRow, error: latestError } = await this.supabase
            .from('cp_kyc_check')
            .select('id, stage, passed, notes, created_at')
            .eq('checked_user_id', userId)
            .or(`claim_id.eq.${claimId},claim_id.is.null`)
            .in('provider', providers)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (latestError) {
            console.error('[MANAGEMENT_STEP] Error fetching latest cp_kyc_check', {
              error: latestError,
              actionId: action.action_id,
              userId,
            });
            return { isComplete: false, ac_status: null, done_action_id: null, status: null, checkedRow: null };
          }

          data = latestRow;
        }
      }

      // Check if KYC was bypassed by an agent
      const currentUser = await this.userService.getCurrentUser();
      const bypassedByAgent = !!(currentUser?.attributes as any)?.kyc?.bypassedByAgent;

      // CPK-3620: um check DONE que FALHOU mas é adiável (notes.postpone_reason.
      // canProceed=true) deixa o cliente prosseguir — tratado como concluído.
      const canProceed = !!data && data.stage === 'DONE' && notesAllowProceed(data.notes);

      // CPK-4051: skip do agente — espelho do gate do backend (prerequisiteCheck.ts,
      // case "KYC"), que já libera a assinatura para esses checks. A linha de skip
      // tem provider='manual' e por isso NUNCA aparece nas queries acima (filtradas
      // por config.providers) — precisa de lookup próprio. Libera a passagem como o
      // postpone; o check segue passed=false e o KYC continua pendente para ops.
      let agentSkip = false;
      if (!(!!data && data.stage === 'DONE' && data.passed === true) && !canProceed) {
        const { data: skipRow, error: skipError } = await this.supabase
          .from('cp_kyc_check')
          .select('id, stage, passed, notes, created_at')
          .eq('checked_user_id', userId)
          .eq('action_id', action.action_id)
          .eq('stage', AGENT_SKIP_STAGE)
          .eq('notes->>flag', AGENT_SKIP_FLAG)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (skipError) {
          console.error('[MANAGEMENT_STEP] Error fetching agent-skip cp_kyc_check', {
            error: skipError,
            actionId: action.action_id,
            userId,
          });
        } else if (skipRow && isAgentSkipCheck(skipRow)) {
          // Revalida stage+flag no cliente (defensivo — o filtro já garante no PostgREST)
          agentSkip = true;
          data = skipRow;
        }
      }

      // CPK-4744: KYC diferido (Simple Questionnaire) — não bloqueia o wizard
      // independente de `data`. ac_status abaixo continua vindo só de `data`,
      // então a pendência real segue visível em outras telas (AC: sem mudança
      // de ac_status/card).
      const deferred = this.isDeferredForClaim(action);

      // Determina ac_status considerando bypass
      let ac_status: string | null = null;
      if (data) {
        if (bypassedByAgent) {
          // Bypassed by agent: tratar como DONE independente de passed
          ac_status = 'DONE';
        } else if (canProceed) {
          // KYC falhou mas é adiável (postpone) — tratar como DONE pro avanço
          ac_status = 'DONE';
        } else if (agentSkip) {
          // CPK-4051: skip do agente — tratar como DONE pro avanço (o check
          // segue passed=false; o KYC continua pendente fora do fluxo)
          ac_status = 'DONE';
        } else if (data.stage === 'DONE' && data.passed === false) {
          // KYC processado mas falhou validação
          ac_status = 'FAILED';
        } else if (data.stage === 'DONE' && data.passed === true) {
          // KYC processado e passou
          ac_status = 'DONE';
        } else {
          // Outros stages (PENDING, PROCESSING, etc)
          ac_status = data.stage;
        }
      }
      
      const done_action_id = data?.id || null;
      const status = ac_status;

      // Lógica de conclusão:
      // - Deferred: Completo independente do resultado do check
      // - Agent mode: Completo se existe um KYC check que não reprovou (qualquer
      //   outro stage, inclusive PROCESSING) — CPK-5056
      // - Bypassed by agent: Completo independente do resultado do check
      // - Normal mode: Completo se stage = DONE E (passed = true OU postpone canProceed),
      //   OU se existe um skip de agente válido (CPK-4051)
      const isComplete = deferred || (isAgentMode
        ? this.isKycCompleteForAgent(done_action_id, ac_status)
        : bypassedByAgent || (!!data && data.stage === 'DONE' && data.passed === true) || canProceed || agentSkip);

      // ✅ Preenche os campos na action
      action.ac_status = ac_status;
      action.done_action_id = done_action_id;
      action.status = status;

      console.log('🔎 [WIZARD_DEBUG] KYC_STATUS_CHECK', {
        actionId: action.action_id,
        actionName: action.action_name,
        userId,
        dbStage: data?.stage,
        dbPassed: data?.passed,
        kycCheckId: data?.id,
        isAgentMode,
        agentClientsList: this.agentClientsService.getAgentClients(),
        agentSkip,
        isComplete,
        reason: agentSkip
          ? 'KYC skipped by agent (CPK-4051) — proceed allowed, check stays pending'
          : data?.stage === 'DONE' ? (data?.passed ? 'KYC completed and passed' : 'KYC completed but FAILED') : 'KYC not complete',
      });

      return { isComplete, ac_status, done_action_id, status, checkedRow: data };
    } catch (error) {
      console.error('[MANAGEMENT_STEP] Exception checking KYC', {
        error,
        actionId: action.action_id,
      });
      return { isComplete: false, ac_status: null, done_action_id: null, status: null, checkedRow: null };
    }
  }

  /**
   * Verifica se assinatura está completa E preenche status na action
   * ✅ Verifica contrato atual E versões anteriores (attributes.previous_versions)
   * Query: claim_id + contract_id + status='SIGNED' → status
   * Obs: signer_id NÃO entra no filtro porque no fluxo PoA o signer é o
   * co-counsel, não o cliente logado.
   */
  private async checkAndFillSignatureStatus(
    action: Action,
    claimId: string,
    _userId: string
  ): Promise<{ isComplete: boolean; ac_status: string | null; done_action_id: string | null; status: string | null }> {
    if (!action.contract_id) {
      console.warn('[MANAGEMENT_STEP] Signature action without contract_id', {
        actionId: action.action_id,
      });
      return { isComplete: false, ac_status: null, done_action_id: null, status: null };
    }

    try {
      // 1️⃣ Busca o contrato atual para obter previous_versions
      const { data: contractData, error: contractError } = await this.supabase
        .from('cp_contract')
        .select('id, attributes')
        .eq('id', action.contract_id)
        .maybeSingle();

      if (contractError) {
        console.error('[MANAGEMENT_STEP] Error fetching contract', {
          error: contractError,
          contractId: action.contract_id,
        });
      }

      // 2️⃣ Monta lista de IDs para verificar (atual + versões anteriores)
      const contractIdsToCheck: string[] = [action.contract_id];
      
      if (contractData?.attributes?.previous_versions && Array.isArray(contractData.attributes.previous_versions)) {
        contractIdsToCheck.push(...contractData.attributes.previous_versions);
        console.log('[MANAGEMENT_STEP] Contract has previous versions', {
          currentContractId: action.contract_id,
          previousVersions: contractData.attributes.previous_versions,
          totalToCheck: contractIdsToCheck.length,
        });
      }

      // 3️⃣ Verifica se existe assinatura para QUALQUER versão do contrato.
      //    NÃO filtrar por signer_id: no fluxo PoA o signer_id é o co-counsel
      //    (que assinou em nome do cliente), não o cliente que está logado.
      //    A combinação (claim_id, contract_id, status='SIGNED') já é
      //    suficiente — cada claim tem 1 assinatura por contrato.
      const { data, error } = await this.supabase
        .from('cp_signed_contract')
        .select('id, status, contract_id')
        .in('contract_id', contractIdsToCheck)
        .eq('claim_id', claimId)
        .eq('status', 'SIGNED')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[MANAGEMENT_STEP] Error checking signature', {
          error,
          contractIds: contractIdsToCheck,
        });
        return { isComplete: false, ac_status: null, done_action_id: null, status: null };
      }

      // CPK-4744: Signature diferida (Simple Questionnaire) — não bloqueia o
      // wizard mesmo sem contrato assinado. ac_status abaixo continua vindo só
      // de `data` (AC: sem mudança de ac_status/card em outras telas).
      const deferred = this.isDeferredForClaim(action);
      const isComplete = deferred || (!!data && data.status === 'SIGNED');
      const ac_status = data?.status || null;
      const done_action_id = data?.id || null;
      const status = data?.status || null;

      // ✅ Preenche os campos na action
      action.ac_status = ac_status;
      action.done_action_id = done_action_id;
      action.status = status;

      console.log('🔎 [WIZARD_DEBUG] SIGNATURE_STATUS_CHECK', {
        actionId: action.action_id,
        actionName: action.action_name,
        currentContractId: action.contract_id,
        totalVersionsChecked: contractIdsToCheck.length,
        signedContractId: data?.contract_id,
        status: data?.status,
        isComplete,
        isPreviousVersion: data?.contract_id !== action.contract_id,
      });

      return { isComplete, ac_status, done_action_id, status };
    } catch (error) {
      console.error('[MANAGEMENT_STEP] Exception checking signature', {
        error,
        contractId: action.contract_id,
      });
      return { isComplete: false, ac_status: null, done_action_id: null, status: null };
    }
  }

  /**
   * Verifica se claim está ativo
   * Retorna false se status = 'INACTIVE'
   */
  private async isClaimActive(claimId: string): Promise<boolean> {
    try {
      const { data, error } = await this.supabase
        .from('cp_claim')
        .select('status')
        .eq('id', claimId)
        .single();

      if (error) {
        console.error('[MANAGEMENT_STEP] Error checking claim status', {
          error,
          claimId,
        });
        return false;
      }

      const isActive = data?.status !== 'INACTIVE';

      console.log('[MANAGEMENT_STEP] Claim active check', {
        claimId,
        status: data?.status,
        isActive,
      });

      return isActive;
    } catch (error) {
      console.error('[MANAGEMENT_STEP] Exception checking claim status', {
        error,
        claimId,
      });
      return false;
    }
  }

  /**
   * Verifica estado completo de todas as actions (para debug/validação)
   * NÃO usar no fluxo normal - faz queries para todas as actions
   */
  async getAllActionsState(
    actions: Action[],
    claimId: string,
    userId: string
  ): Promise<ActionStateResult[]> {
    console.log('[MANAGEMENT_STEP] Getting all actions state (debug mode)', {
      totalActions: actions.length,
    });

    const results: ActionStateResult[] = [];

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      const checkResult = await this.checkAndFillActionStatus(action, claimId, userId);

      results.push({
        actionIndex: i,
        action,
        isComplete: checkResult.isComplete,
        status: checkResult.status,
      });
    }

    return results;
  }
}
